// GameState is the game's simulation of the world between viewscreen
// packets. It owns one SelfState (for the player's own ship) and a Map of
// LocalShip records keyed by viewscreen_id (for other visible ships).
//
// LocalShip shape:
//   {
//     viewscreenId, worldX, worldY, velX, velY,
//     correctionVx, correctionVy, correctionFramesRemaining,
//     rotation,         // radians
//     sprite, shield,
//     lastSeenTick,
//     detail,           // ShipDetail or null
//   }
//
// SelfState shape:
//   {
//     shipID,           // UUID string
//     worldX, worldY, velX, velY,
//     correctionVx, correctionVy, correctionFramesRemaining,
//     packetRotation, prevPacketRotation, prevPrevPacketRotation,
//     hasPacketRotationHistory,   // 0, 1, or 2
//     simulatedRotation, shownRotation,    // radians
//     sprite, shield,
//     detail,
//   }

const CORRECTION_FRAMES = 3
const SHIP_GC_FRAMES = 15

// Defaults used until a ShipDetail arrives over WebSocket.
const DEFAULT_ACCEL = 0.5
const DEFAULT_MAX_SPEED = 40.0
const DEFAULT_TURN_RATE = 0.08

// Tolerance for "packet rotation unchanged": one u16 wire step is 2π/65536
// rad; two wire steps is our tolerance for the drift-snap rule.
const ROTATION_UNCHANGED_TOL = (2 * Math.PI) / 65536 * 1.5

const SPRITE_ROCKET_FLAG = 1 << 6

export class GameState {
  constructor() {
    this.self = null
    this.ships = new Map()
    this.gameFrame = 0
    this.packetsReceived = 0
    this.packetsDropped = 0
    this.lastPacketFrame = -1
    this.serverTick = 0
    this.latestServerTick = 0
    // Iteration 1.9: latestServerTick is the global time-axis tip — advanced
    // by EITHER ship viewscreen OR projectile packets. It drives viewTick.
    // Per-stream stale-detection uses its own counter; the two streams must
    // not interfere with each other's drop logic, since a same-tick pair
    // (viewscreen + projectile both emitted in one server network tick) is
    // expected and both should be processed.
    this.latestViewscreenTick = 0
    this.frameWhenLatestObserved = 0
    this.viewTick = 0
  }

  // noteServerTick advances the global time-axis tip if `serverTick` is
  // larger. Never drops anything; idempotent. Called by both the viewscreen
  // ingest path (after stale-detection) and the projectile ingest path
  // (which has no stale-detection — projectiles are idempotent on receive).
  noteServerTick(serverTick) {
    if (serverTick > this.latestServerTick) {
      this.latestServerTick = serverTick
      this.frameWhenLatestObserved = this.gameFrame
      this.serverTick = serverTick
    }
  }

  initSelf(shipID) {
    this.self = {
      shipID,
      worldX: 0,
      worldY: 0,
      velX: 0,
      velY: 0,
      correctionVx: 0,
      correctionVy: 0,
      correctionFramesRemaining: 0,
      packetRotation: 0,
      prevPacketRotation: 0,
      prevPrevPacketRotation: 0,
      hasPacketRotationHistory: 0,
      simulatedRotation: 0,
      shownRotation: 0,
      sprite: 0,
      shield: 0,
      detail: null,
    }
  }

  ingestShipDetail(d) {
    // viewscreen_id === 0 is the Shape-B "self / no slot" binding. This is
    // the FIRST time the game learns its shipID.
    if (d.viewscreen_id === 0) {
      if (!this.self) {
        this.initSelf(d.ship_id)
      }
      if (this.self) {
        this.self.detail = d
      }
      return
    }
    const s = this.ships.get(d.viewscreen_id)
    if (s) {
      s.detail = d
    }
    // If a detail arrives before the first viewscreen packet reveals this
    // slot, we drop it. In practice the packet with this slot arrives in
    // the same server tick.
  }

  ingestPacket(header, records) {
    // Drop reordered / duplicate VIEWSCREEN packets. Strict less-than-or-
    // equal so we drop same-tick duplicates (viewscreen retransmits don't
    // exist server-side, but the same tick from a delayed reorder shouldn't
    // overwrite). The projectile stream's same-tick packet is allowed
    // through this gate because it uses its own (unbounded) ingest path.
    if (header.serverTick <= this.latestViewscreenTick) {
      this.packetsDropped++
      return
    }
    this.latestViewscreenTick = header.serverTick
    this.noteServerTick(header.serverTick)
    this.packetsReceived++
    this.lastPacketFrame = this.gameFrame

    // --- Update self from header ---
    if (this.self) {
      const self = this.self
      const errX = header.selfWorldX - self.worldX
      const errY = header.selfWorldY - self.worldY
      self.correctionVx = errX / CORRECTION_FRAMES
      self.correctionVy = errY / CORRECTION_FRAMES
      self.correctionFramesRemaining = CORRECTION_FRAMES
      // Velocity and shield are authoritative from the header.
      self.velX = header.selfVelX
      self.velY = header.selfVelY
      self.shield = header.selfShield
      self.sprite = header.selfSprite
      // Rotation history for drift-snap.
      self.prevPrevPacketRotation = self.prevPacketRotation
      self.prevPacketRotation = header.selfRotation
      self.packetRotation = header.selfRotation
      if (self.hasPacketRotationHistory < 2) self.hasPacketRotationHistory++
      // Drift-snap: if the last two packet rotations are effectively equal,
      // snap simulatedRotation to packetRotation.
      if (
        self.hasPacketRotationHistory >= 2 &&
        angleClose(self.prevPacketRotation, self.prevPrevPacketRotation, ROTATION_UNCHANGED_TOL)
      ) {
        self.simulatedRotation = self.packetRotation
      }
    }

    // --- Update other ships from array (scanner-relative → world) ---
    for (const r of records) {
      const targetWorldX = header.selfWorldX + r.posX / 4  // PosWireScale
      const targetWorldY = header.selfWorldY + r.posY / 4
      const targetVelX = r.velX / 10  // VelWireScale
      const targetVelY = r.velY / 10
      const targetRotation = (r.rotation / 256) * 2 * Math.PI

      const cur = this.ships.get(r.viewscreenId)
      if (cur) {
        const errX = targetWorldX - cur.worldX
        const errY = targetWorldY - cur.worldY
        cur.correctionVx = errX / CORRECTION_FRAMES
        cur.correctionVy = errY / CORRECTION_FRAMES
        cur.correctionFramesRemaining = CORRECTION_FRAMES
        cur.velX = targetVelX
        cur.velY = targetVelY
        cur.rotation = targetRotation // snap
        cur.sprite = r.sprite
        cur.shield = r.shield
        cur.lastSeenTick = this.gameFrame
      } else {
        this.ships.set(r.viewscreenId, {
          viewscreenId: r.viewscreenId,
          worldX: targetWorldX,
          worldY: targetWorldY,
          velX: targetVelX,
          velY: targetVelY,
          correctionVx: 0,
          correctionVy: 0,
          correctionFramesRemaining: 0,
          rotation: targetRotation,
          sprite: r.sprite,
          shield: r.shield,
          lastSeenTick: this.gameFrame,
          detail: null,
        })
      }
    }
  }

  tick(kb) {
    this.gameFrame++
    // Iteration 1.9: viewTick advances at 60 fps between packets.
    this.viewTick = this.latestServerTick + (this.gameFrame - this.frameWhenLatestObserved)

    // --- Self ---
    if (this.self) {
      const self = this.self
      const turnRate = self.detail ? self.detail.turn_rate : DEFAULT_TURN_RATE
      if (kb.rotateLeftHeld)  self.simulatedRotation = wrapAngle(self.simulatedRotation + turnRate)
      if (kb.rotateRightHeld) self.simulatedRotation = wrapAngle(self.simulatedRotation - turnRate)
      // shownRotation is the circular (vector-sum) mean of the two. Handles
      // the 0/2π seam correctly.
      const a = self.packetRotation
      const b = self.simulatedRotation
      self.shownRotation = Math.atan2(
        Math.sin(a) + Math.sin(b),
        Math.cos(a) + Math.cos(b),
      )

      if ((self.sprite & SPRITE_ROCKET_FLAG) !== 0) {
        const accel = self.detail ? self.detail.accel : DEFAULT_ACCEL
        self.velX += accel * Math.cos(self.shownRotation)
        self.velY += accel * Math.sin(self.shownRotation)
        const maxSpeed = self.detail ? self.detail.max_speed : DEFAULT_MAX_SPEED
        const sp = Math.hypot(self.velX, self.velY)
        if (sp > maxSpeed) {
          self.velX *= maxSpeed / sp
          self.velY *= maxSpeed / sp
        }
      }

      self.worldX += self.velX + self.correctionVx
      self.worldY += self.velY + self.correctionVy
      if (self.correctionFramesRemaining > 0) {
        self.correctionFramesRemaining--
        if (self.correctionFramesRemaining === 0) {
          self.correctionVx = 0
          self.correctionVy = 0
        }
      }
    }

    // --- Other ships ---
    for (const s of this.ships.values()) {
      if ((s.sprite & SPRITE_ROCKET_FLAG) !== 0) {
        const accel = s.detail ? s.detail.accel : DEFAULT_ACCEL
        s.velX += accel * Math.cos(s.rotation)
        s.velY += accel * Math.sin(s.rotation)
        const maxSpeed = s.detail ? s.detail.max_speed : DEFAULT_MAX_SPEED
        const sp = Math.hypot(s.velX, s.velY)
        if (sp > maxSpeed) {
          s.velX *= maxSpeed / sp
          s.velY *= maxSpeed / sp
        }
      }
      s.worldX += s.velX + s.correctionVx
      s.worldY += s.velY + s.correctionVy
      if (s.correctionFramesRemaining > 0) {
        s.correctionFramesRemaining--
        if (s.correctionFramesRemaining === 0) {
          s.correctionVx = 0
          s.correctionVy = 0
        }
      }
    }

    // GC ships not seen in too many frames.
    for (const [id, s] of this.ships) {
      if (this.gameFrame - s.lastSeenTick > SHIP_GC_FRAMES) {
        this.ships.delete(id)
      }
    }
  }
}

function wrapAngle(a) {
  const twoPi = 2 * Math.PI
  let x = a % twoPi
  if (x < 0) x += twoPi
  return x
}

function angleClose(a, b, tol) {
  const twoPi = 2 * Math.PI
  let d = Math.abs(a - b) % twoPi
  if (d > Math.PI) d = twoPi - d
  return d < tol
}
